// policy-adapter.ts
//
// Cổng quyết định cho MỌI thao tác quan trọng của supervisor (§9.3):
//   input {actor, operation, repo, candidate/base SHA, policy digest, authority_ref,
//          review_state_root} → allow | deny | requires_authority + reason.
//
// Ba luật cứng:
//  1. ĐỌC VERDICT JSON, KHÔNG tin exit code. Đo được trong pack hiện tại: một số hook
//     `deny` mà vẫn exit 0 (và ngược lại). Tin exit code ⇒ fail-open âm thầm.
//  2. Parser hỏng / facade lỗi / verdict không parse được ⇒ DENY cho mutation quan
//     trọng. Không bao giờ "không hiểu thì cho qua".
//  3. TÁCH scan/review (lấy evidence) khỏi GHI state (controller sở hữu). Adapter này
//     KHÔNG gọi wrapper nào ghi vào source repo — mọi state đi qua `review_state_root`
//     truyền tường minh (né `state_home.py` resolve worktree về repo chính).
//
// ⚠️ GIẢ ĐỊNH ĐƯỢC GHI RÕ (xem evidence report §open items): pack hiện chưa có script
// facade nào được ghim ổn định cho autopilot, nên adapter gọi facade qua SUBPROCESS
// với giao diện khai báo dưới đây và test bằng fixture giả. Khi P4/P5 chốt script
// thật, chỉ cần trỏ `facadeCommand` — KHÔNG phải sửa logic quyết định.

import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";
import { classifyPushCommand } from "./git-push-command-classifier.js";
import { normalizeReason, type PolicyVerdict, parseVerdict } from "./policy-verdict-parser.js";

export type { PolicyDecision, PolicyVerdict } from "./policy-verdict-parser.js";
// Re-export để call site chỉ cần nhớ MỘT module cho policy.
export { normalizeReason } from "./policy-verdict-parser.js";

import {
  type ReceiptExpectation,
  readReviewReceipt,
  receiptOpensGate,
} from "./review-state-store.js";

export interface PolicyAdapterInput {
  actor: "supervisor";
  operation: string;
  repo: string;
  candidateSha?: string;
  baseSha?: string;
  policyDigest: string;
  authorityRef?: string;
  reviewStateRoot: string;
  missionId: string;
  /** Dòng lệnh sắp chạy (nếu có) — dùng cho phân loại push thật/giả. */
  command?: string;
}

/** Thao tác làm THAY ĐỔI thế giới ⇒ bắt buộc fail-closed khi không chắc. */
export const IMPORTANT_MUTATIONS = new Set([
  "git_push",
  "git_commit",
  "git_merge",
  "deploy",
  "migration",
  "mm_post",
  "pr_create",
]);

/** Worker KHÔNG được tự phê duyệt / tự commit / tự deploy (§9.3, S3b). */
const WORKER_FORBIDDEN_OPERATIONS = new Set([
  "approve",
  "self_approve",
  "control_cli",
  "gate_override",
  "deploy",
]);

export interface FacadeResult {
  status: number | null;
  stdout: string;
}

export interface PolicyAdapterDeps {
  /** Lệnh facade đã GHIM theo pack version của mission. */
  facadeCommand?: { command: string; args: string[] };
  runFacade?: (input: PolicyAdapterInput) => Promise<FacadeResult>;
  timeoutMs?: number;
}

async function defaultRunFacade(
  input: PolicyAdapterInput,
  deps: PolicyAdapterDeps,
): Promise<FacadeResult> {
  if (deps.facadeCommand === undefined) {
    throw new Error("policy-adapter: chưa cấu hình `facadeCommand` cho mission này.");
  }
  const res = await spawnAsyncCapture(
    deps.facadeCommand.command,
    [...deps.facadeCommand.args, JSON.stringify(input)],
    { timeoutMs: deps.timeoutMs ?? 30_000, forwardSigint: false },
  );
  return { status: res.status, stdout: res.stdout };
}

/**
 * Quyết định cho một thao tác. Thứ tự: forbidden scope → review gate (push) → facade.
 * Bất kỳ bước nào không kết luận được ⇒ deny (với mutation quan trọng).
 */
export async function evaluatePolicy(
  input: PolicyAdapterInput,
  deps: PolicyAdapterDeps = {},
): Promise<PolicyVerdict> {
  const isMutation = IMPORTANT_MUTATIONS.has(input.operation);

  // 1. Phạm vi cấm tuyệt đối — worker không tự phê duyệt/deploy được.
  if (WORKER_FORBIDDEN_OPERATIONS.has(input.operation)) {
    return {
      decision: "deny",
      reason: normalizeReason(
        `Thao tác "${input.operation}" nằm trong phạm vi CẤM của worker: ` +
          "phê duyệt/commit/deploy là quyền của controller + con người, không của phiên worker.",
      ),
      source: "forbidden-scope",
    };
  }

  // 2. Lệnh có thực sự là `git push` không (không grep chuỗi — S3b test 56-58).
  if (input.command !== undefined) {
    const classified = classifyPushCommand(input.command);
    if (classified.isPush && input.operation !== "git_push") {
      return {
        decision: "deny",
        reason: normalizeReason(
          `Lệnh chứa \`git push\` thật (${classified.matchedSegment ?? "?"}) nhưng operation ` +
            `khai báo là "${input.operation}" ⇒ khai sai phạm vi, chặn.`,
        ),
        source: "forbidden-scope",
      };
    }
  }

  // 3. Push phải có review receipt KHỚP mission/repo/head/base/policy.
  if (input.operation === "git_push") {
    if (input.candidateSha === undefined || input.baseSha === undefined) {
      return {
        decision: "deny",
        reason: normalizeReason("Push thiếu candidate/base SHA ⇒ không xác thực được review."),
        source: "review-gate",
      };
    }
    const expectation: ReceiptExpectation = {
      missionId: input.missionId,
      repoPath: input.repo,
      candidateSha: input.candidateSha,
      baseSha: input.baseSha,
      policyDigest: input.policyDigest,
    };
    const receipt = await readReviewReceipt(
      input.reviewStateRoot,
      input.repo,
      input.candidateSha,
    ).catch(() => undefined);
    const check = receiptOpensGate(receipt, expectation);
    if (!check.ok) {
      return { decision: "deny", reason: normalizeReason(check.reason), source: "review-gate" };
    }
  }

  // 4. Facade của pack — ĐỌC VERDICT JSON, không tin exit code.
  let result: FacadeResult;
  try {
    result = deps.runFacade ? await deps.runFacade(input) : await defaultRunFacade(input, deps);
  } catch (err) {
    return failClosed(isMutation, `Facade policy lỗi khi chạy: ${(err as Error).message}`);
  }

  const verdict = parseVerdict(result.stdout);
  if (verdict === undefined) {
    return failClosed(
      isMutation,
      `Không đọc được verdict JSON từ facade (exit=${result.status}). ` +
        "Exit code KHÔNG được dùng thay verdict.",
    );
  }
  return verdict;
}

function failClosed(isMutation: boolean, reason: string): PolicyVerdict {
  return {
    decision: isMutation ? "deny" : "requires_authority",
    reason: normalizeReason(
      `${reason} ⇒ ${isMutation ? "FAIL-CLOSED (deny)" : "cần thẩm quyền xác nhận"} (§9.3).`,
    ),
    source: "adapter-error",
  };
}
